export const SUPPORTED_LANGUAGES = ["tr", "en"];

export const EMPTY_FORM = {
  revision: null,
  name: "",
  match: "exact",
  category: "",
  provider: "",
  domain: "",
  privacyLink: "",
  status: "quarantined",
  researchStatus: "pending-review",
  reviewReason: "",
  translations: {
    tr: { description: "", duration: "" },
    en: { description: "", duration: "" },
  },
};

const clean = (value) => String(value || "").trim();

export function getRequiredLanguages(item) {
  const languages = (item?.languages || [])
    .map((row) => clean(row.language).toLowerCase())
    .filter((language) => SUPPORTED_LANGUAGES.includes(language));
  return [...new Set(languages)];
}

export function formFromUnknown(item) {
  const translations = { ...EMPTY_FORM.translations };
  for (const row of item?.languages || []) {
    const language = clean(row.language).toLowerCase();
    if (!SUPPORTED_LANGUAGES.includes(language)) continue;
    translations[language] = {
      description: clean(row.description),
      duration: clean(row.duration),
    };
  }

  return {
    ...EMPTY_FORM,
    name: clean(item?.name),
    category: clean(item?.category),
    provider: clean(item?.provider),
    domain: "",
    status: "active",
    researchStatus: "primary-source-reviewed",
    translations,
  };
}

export function formFromEntry(entry) {
  const translations = { ...EMPTY_FORM.translations };
  for (const row of entry?.translations || []) {
    const language = clean(row.language).toLowerCase();
    if (!SUPPORTED_LANGUAGES.includes(language)) continue;
    translations[language] = {
      description: clean(row.description),
      duration: clean(row.duration),
    };
  }

  return {
    ...EMPTY_FORM,
    revision: Number.isInteger(entry?.revision) ? entry.revision : null,
    name: clean(entry?.name),
    match: clean(entry?.match) || "exact",
    category: clean(entry?.category),
    provider: clean(entry?.provider),
    domain: clean(entry?.domain),
    privacyLink: clean(entry?.privacyLink),
    status: clean(entry?.status) || "active",
    researchStatus: clean(entry?.researchStatus),
    reviewReason: clean(entry?.reviewReason),
    translations,
  };
}

export function validateKnowledgeBaseForm(form, requiredLanguages = []) {
  const errors = [];
  if (!clean(form.name)) errors.push("Çerez adı zorunludur.");
  if (!clean(form.category)) errors.push("Kategori zorunludur.");
  if (!clean(form.researchStatus)) errors.push("Araştırma durumu zorunludur.");
  if (form.status === "active" && form.researchStatus === "pending-review") {
    errors.push("İncelemesi bekleyen kayıt aktif olamaz.");
  }

  const hasCompleteTranslation = SUPPORTED_LANGUAGES.some((language) => {
    const translation = form.translations?.[language] || {};
    return Boolean(clean(translation.description) && clean(translation.duration));
  });
  if (!hasCompleteTranslation) errors.push("En az bir dilde açıklama ve süre zorunludur.");

  for (const language of requiredLanguages) {
    const translation = form.translations?.[language] || {};
    if (!clean(translation.description) || !clean(translation.duration)) {
      errors.push(`${language.toUpperCase()} açıklama ve süre zorunludur.`);
    }
  }

  for (const language of SUPPORTED_LANGUAGES) {
    const translation = form.translations?.[language] || {};
    const hasDescription = Boolean(clean(translation.description));
    const hasDuration = Boolean(clean(translation.duration));
    if (hasDescription !== hasDuration) {
      errors.push(`${language.toUpperCase()} açıklama ve süre birlikte doldurulmalıdır.`);
    }
  }
  return errors;
}

export function buildKnowledgeBasePayload(form) {
  const translations = {};
  for (const language of SUPPORTED_LANGUAGES) {
    const description = clean(form.translations?.[language]?.description);
    const duration = clean(form.translations?.[language]?.duration);
    if (description && duration) translations[language] = { description, duration };
  }

  return {
    name: clean(form.name),
    match: form.match,
    category: form.category,
    provider: clean(form.provider) || null,
    domain: clean(form.domain) || null,
    privacyLink: clean(form.privacyLink) || null,
    status: form.status,
    researchStatus: clean(form.researchStatus) || null,
    reviewReason: clean(form.reviewReason) || null,
    translations,
  };
}
